import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Fade from "@mui/material/Fade";
import Divider from "@mui/material/Divider";
import { alpha } from "@mui/material/styles";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import BoltIcon from "@mui/icons-material/Bolt";
import SpeedIcon from "@mui/icons-material/Speed";
import BarChartIcon from "@mui/icons-material/BarChart";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import ErrorBoundary from "components/ErrorBoundary";

// Services
import { RequestService } from "services/RequestService";

function UpdateForm({ open, onClose, onSuccess, transaction }) {
  const [stUnits, setStUnits] = useState([]);
  const [gtUnits, setGtUnits] = useState([]);
  const [gtStatuses, setGtStatuses] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [notification, setNotification] = useState({
    color: "info",
    icon: "notifications",
    title: "تنبيه",
    content: "",
    dateTime: "",
    open: false,
  });

  const closeNotification = () => setNotification({ ...notification, open: false });

  const [formData, setFormData] = useState({
    powerStationId: "",
    powerGenerator: "",
    target: "",
    gasPressure: "",
    gasVolume: "",
    frequency: "",
    stReadings: []
  });

  useEffect(() => {
    if (open && transaction) {
      // Reset form data when dialog opens
      setFormData({
        powerStationId: transaction.powerStationId?.toString() || "",
        powerGenerator: transaction.powerGenerator?.toString() || "",
        target: transaction.target?.toString() || "",
        gasPressure: transaction.gasPressure?.toString() || "",
        gasVolume: transaction.gasVolume?.toString() || "",
        frequency: transaction.frequency?.toString() || "",
        stReadings: []
      });
      
      // Fetch all required data
      fetchStUnits();
      fetchGtUnits();
      fetchGtStatuses();
    }
  }, [open, transaction]);

  useEffect(() => {
    if (open && transaction && stUnits.length > 0 && gtUnits.length > 0 && gtStatuses.length > 0) {
      // Initialize form data with transaction values
      const stReadings = stUnits.map(st => {
        // Find existing ST reading from transaction
        const existingStReading = transaction.stReadings?.find(
          reading => reading.stName === st.name || reading.stId === st.id
        );
        
        // Get all GT units for this ST unit
        const stGtUnits = gtUnits.filter(gt => gt.stId === st.id);
        
        const gtReadings = stGtUnits.map(gt => {
          // Find existing GT reading from transaction
          const existingGtReading = existingStReading?.gtReadings?.find(
            reading => reading.gtName === gt.name || reading.gtId === gt.id
          );
          
          // Find status ID by status name
          const gtStatus = gtStatuses.find(
            status => status.name === existingGtReading?.statusName
          );
          
          return {
            gtId: parseInt(gt.id),
            gtValue: existingGtReading?.gtValue?.toString() || "0",
            gtStatusId: gtStatus?.id || gtStatuses[0]?.id || "",
            gtName: gt.name || ""
          };
        });
        
        return {
          stId: parseInt(st.id),
          stValue: existingStReading?.stValue?.toString() || "0",
          expectedSTValue: existingStReading?.expectedSTValue?.toString() || "0",
          stName: st.name || "",
          gtReadings
        };
      });
      
      setFormData(prev => ({
        ...prev,
        stReadings
      }));
    }
  }, [open, transaction, stUnits, gtUnits, gtStatuses]);

  const handleStValueChange = (stId, value) => {
    setFormData(prev => ({
      ...prev,
      stReadings: prev.stReadings.map(st =>
        st.stId === stId ? { ...st, stValue: value } : st
      )
    }));
  };

  const handleGtValueChange = (stId, gtId, value) => {
    setFormData(prev => ({
      ...prev,
      stReadings: prev.stReadings.map(st =>
        st.stId === stId
          ? {
              ...st,
              gtReadings: st.gtReadings.map(gt =>
                gt.gtId === gtId ? { ...gt, gtValue: value } : gt
              )
            }
          : st
      )
    }));
  };

  const handleGtStatusChange = (stId, gtId, statusId) => {
    setFormData(prev => ({
      ...prev,
      stReadings: prev.stReadings.map(st =>
        st.stId === stId
          ? {
              ...st,
              gtReadings: st.gtReadings.map(gt =>
                gt.gtId === gtId ? { ...gt, gtStatusId: statusId } : gt
              )
            }
          : st
      )
    }));
  };

  const fetchStUnits = async () => {
    try {
      const response = await RequestService.get(`/ST/${transaction.powerStationId}/sts`);
      if (response.data && !response.data.error) {
        setStUnits(response.data.data || []);
      } else {
        setNotification({
          color: "error",
          icon: "warning",
          title: "فشلت العملية",
          content: response.data?.message || "فشل في تحميل وحدات التوربين البخاري",
          open: true
        });
      }
    } catch (error) {
      console.error("Error fetching ST units:", error);
      setNotification({
        color: "error",
        icon: "warning",
        title: "فشلت العملية",
        content: error.response?.data?.message || "حدث خطأ أثناء تحميل وحدات التوربين البخاري",
        open: true
      });
    }
  };

  const fetchGtUnits = async () => {
    try {
      const response = await RequestService.get(`/GT/${transaction.powerStationId}/gts`);
      if (response.data && !response.data.error) {
        setGtUnits(response.data.data || []);
      } else {
        setNotification({
          color: "error",
          icon: "warning",
          title: "فشلت العملية",
          content: response.data?.message || "فشل في تحميل الوحدات الغازية",
          open: true
        });
      }
    } catch (error) {
      console.error("Error fetching GT units:", error);
      setGtUnits([]);
      setNotification({
        color: "error",
        icon: "warning",
        title: "فشلت العملية",
        content: error.response?.data?.message || "حدث خطأ أثناء تحميل الوحدات الغازية",
        open: true
      });
    }
  };

  const fetchGtStatuses = async () => {
    try {
      const response = await RequestService.get("PowerStation/gt-statuses");
      if (response.data && !response.data.error) {
        setGtStatuses(response.data.data || []);
      } else {
        setNotification({
          color: "error",
          icon: "warning",
          title: "فشلت العملية",
          content: response.data?.message || "فشل في تحميل حالات الوحدات الغازية",
          open: true
        });
      }
    } catch (error) {
      console.error("Error fetching GT statuses:", error);
      setNotification({
        color: "error",
        icon: "warning",
        title: "فشلت العملية",
        content: error.response?.data?.message || "حدث خطأ أثناء تحميل حالات الوحدات الغازية",
        open: true
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      const payload = {
        powerStationId: parseInt(transaction.powerStationId),
        powerGenerator: formData.powerGenerator ? parseFloat(formData.powerGenerator) : 0,
        target: formData.target ? parseFloat(formData.target) : 0,
        gasPressure: formData.gasPressure ? parseFloat(formData.gasPressure) : 0,
        gasVolume: formData.gasVolume ? parseFloat(formData.gasVolume) : 0,
        frequency: formData.frequency ? parseFloat(formData.frequency) : 0,
        stReadings: formData.stReadings.map(stReading => ({
          stId: parseInt(stReading.stId),
          stValue: stReading.stValue ? parseFloat(stReading.stValue) : 0,
          expectedSTValue: stReading.expectedSTValue ? parseFloat(stReading.expectedSTValue) : 0,
          gtReadings: stReading.gtReadings.map(gt => ({
            gtId: parseInt(gt.gtId),
            gtValue: gt.gtValue ? parseFloat(gt.gtValue) : 0,
            gtStatusId: parseInt(gt.gtStatusId),
            gtName: gt.gtName
          }))
        }))
      };

      const response = await RequestService.put(`transaction/${transaction.id}`, payload);
      
      if (response.data && !response.data.error) {
        setNotification({
          color: "success",
          icon: "check",
          title: "تمت العملية بنجاح",
          content: response.data?.message || "تم تحديث القراءة بنجاح",
          open: true
        });
        if (onSuccess) {
          onSuccess(response);
        }
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        throw new Error(response.data?.message || "فشل في تحديث القراءة");
      }
    } catch (error) {
      console.error("Error updating transaction:", error);
      
      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        const newErrors = {};
        
        Object.keys(backendErrors).forEach(key => {
          const match = key.match(/STReadings\[(\d+)\]\.(\w+)/);
          if (match) {
            const [_, index, field] = match;
            const stId = formData.stReadings[parseInt(index)]?.stId;
            if (stId) {
              const errorKey = `${field.toLowerCase()}_${stId}`;
              newErrors[errorKey] = backendErrors[key][0];
            }
          } else {
            const fieldName = key.charAt(0).toLowerCase() + key.slice(1);
            newErrors[fieldName] = backendErrors[key][0];
          }
        });
        
        setErrors(newErrors);
        setNotification({
          color: "error",
          icon: "warning",
          title: "فشلت العملية",
          content: "يرجى تصحيح الأخطاء في النموذج",
          open: true
        });
      } else {
        setNotification({
          color: "error",
          icon: "warning",
          title: "فشلت العملية",
          content: error.response?.data?.message || "حدث خطأ أثناء تحديث القراءة",
          open: true
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <ErrorBoundary>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            overflowY: 'auto',
            m: 2,
            borderRadius: 1,
          }
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 1 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox display="flex" alignItems="center">
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="3.5rem"
                height="3.5rem"
                variant="gradient"
                bgColor="info"
                color="white"
                shadow="md"
                borderRadius="xl"
                mr={2}
              >
                <BoltIcon sx={{ fontSize: 28 }} />
              </MDBox>
              <MDBox>
                <MDTypography variant="h3" fontWeight="medium" color="info" mb={0.5}>
                  تحديث القراءة / Update Transaction
                </MDTypography>
                <MDTypography variant="body2" color="text" opacity={0.8}>
                  قم بتحديث تفاصيل القراءة / Update transaction details
                </MDTypography>
              </MDBox>
            </MDBox>
            <IconButton 
              onClick={onClose}
              size="large"
              sx={{
                color: 'text.secondary',
                transition: 'all 0.2s',
                '&:hover': {
                  color: 'error.main',
                  transform: 'rotate(90deg)',
                },
              }}
            >
              <CloseIcon fontSize="large" />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 2 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Measurements Section */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <MDBox mb={2}>
                      <MDInput
                        type="number"
                        label="التردد / Frequency"
                        name="frequency"
                        value={formData.frequency}
                        onChange={handleInputChange}
                        error={!!errors.frequency}
                        helperText={errors.frequency}
                        inputProps={{
                          step: "0.01",
                          min: "45",
                          max: "65"
                        }}
                        required
                        fullWidth
                        disabled={loading}
                        InputProps={{
                          startAdornment: (
                            <SpeedIcon sx={{ color: 'text.secondary', mr: 1 }} />
                          ),
                        }}
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDBox mb={2}>
                      <MDInput
                        type="number"
                        label="القيمة المستهدفة / Target Value"
                        name="target"
                        value={formData.target}
                        onChange={handleInputChange}
                        error={!!errors.target}
                        helperText={errors.target}
                        inputProps={{
                          step: "0.01",
                          min: "0.01"
                        }}
                        required
                        fullWidth
                        disabled={loading}
                        InputProps={{
                          startAdornment: (
                            <BarChartIcon sx={{ color: 'text.secondary', mr: 1 }} />
                          ),
                        }}
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDBox mb={2}>
                      <MDInput
                        type="number"
                        label="ضغط الغاز / Gas Pressure"
                        name="gasPressure"
                        value={formData.gasPressure}
                        onChange={handleInputChange}
                        error={!!errors.gasPressure}
                        helperText={errors.gasPressure}
                        inputProps={{
                          step: "0.01",
                          min: "0.01"
                        }}
                        required
                        fullWidth
                        disabled={loading}
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDBox mb={2}>
                      <MDInput
                        type="number"
                        label="تدفق الغاز (م³/ساعة) / Gas Flow"
                        name="gasVolume"
                        value={formData.gasVolume}
                        onChange={handleInputChange}
                        error={!!errors.gasVolume}
                        helperText={errors.gasVolume}
                        inputProps={{
                          step: "0.01",
                          min: "0.01"
                        }}
                        required
                        fullWidth
                        disabled={loading}
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDBox mb={2}>
                      <MDInput
                        type="number"
                        label="مولد الطاقة / Power Generator"
                        name="powerGenerator"
                        value={formData.powerGenerator}
                        onChange={handleInputChange}
                        error={!!errors.powerGenerator}
                        helperText={errors.powerGenerator}
                        inputProps={{
                          step: "0.01",
                          min: "0.01"
                        }}
                        required
                        fullWidth
                        disabled={loading}
                      />
                    </MDBox>
                  </Grid>

                  {/* ST Units Section */}
                  {formData.stReadings.map((stReading) => {
                    const st = stUnits.find(unit => unit.id === stReading.stId);
                    return (
                      <Grid item xs={12} key={stReading.stId}>
                        <MDBox mb={3} sx={{ 
                          p: 2, 
                          border: '1px solid',
                          borderColor: 'info.main',
                          borderRadius: 1,
                          '&:hover': {
                            boxShadow: '0 0 10px rgba(26, 115, 232, 0.1)'
                          }
                        }}>
                          <MDTypography variant="h6" mb={2} color="info.main">
                            {st?.name || `Steam Turbine ${stReading.stId}`}
                          </MDTypography>
                          
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <MDInput
                                type="number"
                                label="Steam Turbine Value"
                                value={stReading.stValue}
                                onChange={(e) => handleStValueChange(stReading.stId, e.target.value)}
                                error={!!errors[`stValue_${stReading.stId}`]}
                                helperText={errors[`stValue_${stReading.stId}`]}
                                inputProps={{
                                  step: "any",
                                  min: "0"
                                }}
                                required
                                fullWidth
                                disabled={loading}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <MDInput
                                type="number"
                                label="Expected Steam Turbine Value"
                                value={stReading.expectedSTValue}
                                onChange={(e) => {
                                  setFormData(prev => ({
                                    ...prev,
                                    stReadings: prev.stReadings.map(st =>
                                      st.stId === stReading.stId 
                                        ? { ...st, expectedSTValue: e.target.value } 
                                        : st
                                    )
                                  }));
                                  if (errors[`expectedstvalue_${stReading.stId}`]) {
                                    setErrors(prev => ({ ...prev, [`expectedstvalue_${stReading.stId}`]: undefined }));
                                  }
                                }}
                                error={!!errors[`expectedstvalue_${stReading.stId}`]}
                                helperText={errors[`expectedstvalue_${stReading.stId}`]}
                                inputProps={{
                                  step: "any",
                                  min: "0"
                                }}
                                required
                                fullWidth
                                disabled={loading}
                              />
                            </Grid>
                          </Grid>

                          <MDBox mt={2}>
                            <MDTypography variant="subtitle2" mb={1} color="info">
                              Gas Turbines:
                            </MDTypography>
                            <Grid container spacing={2}>
                              {stReading.gtReadings.map((gtReading) => {
                                const gt = gtUnits.find(unit => unit.id === gtReading.gtId);
                                return (
                                  <Grid item xs={12} md={6} key={gtReading.gtId}>
                                    <MDBox mb={2}>
                                      <MDInput
                                        type="number"
                                        label={gt?.name}
                                        value={gtReading.gtValue}
                                        onChange={(e) => handleGtValueChange(stReading.stId, gtReading.gtId, e.target.value)}
                                        error={!!errors[`gtValue_${gtReading.gtId}`]}
                                        helperText={errors[`gtValue_${gtReading.gtId}`]}
                                        inputProps={{
                                          step: "any",
                                          min: "0"
                                        }}
                                        required
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            height: '45px'
                                          }
                                        }}
                                        fullWidth
                                        disabled={loading}
                                      />
                                    </MDBox>
                                    <FormControl fullWidth>
                                      <InputLabel>Gas Turbine Status</InputLabel>
                                      <Select
                                        value={gtReading.gtStatusId || ""}
                                        onChange={(e) => handleGtStatusChange(stReading.stId, gtReading.gtId, e.target.value)}
                                        label="Gas Turbine Status"
                                        error={!!errors[`gtStatus_${gtReading.gtId}`]}
                                        style={{ height: '45px' }}
                                        disabled={loading}
                                        required
                                      >
                                        {gtStatuses.map((status) => (
                                          <MenuItem key={status.id} value={status.id}>
                                            {status.name}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </MDBox>
                        </MDBox>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, justifyContent: "space-between" }}>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSubmit}
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            حفظ / Save
          </MDButton>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={onClose}
            startIcon={<CancelIcon />}
            disabled={loading}
          >
            إلغاء / Cancel
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        dateTime={notification.dateTime}
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
      />
    </ErrorBoundary>
  );
}

UpdateForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  transaction: PropTypes.object,
};

export default UpdateForm;